import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, RefreshCw, Package, Clock, CheckCircle, AlertCircle, Truck, Archive, ArrowRight, Filter, Eye, Plus, Ship, FileText, PlayCircle, Users, Loader2, ScanLine } from 'lucide-react';
import { POrdersDto, POrdersListItem, POrderStatus, InboundShipmentSummaryDto } from '../../../../types/porders';
import { pOrdersService } from '../../../../services/pOrdersService';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { bilmottakService, ActiveBilmottakSessionDto } from '../../../../services/bilmottakService';
import { BackButton } from '../../../common/BackButton';
import { PreviewPOrderModal } from './PreviewPOrderModal';
import { NewBilmottakModal } from './NewBilmottakModal';
import { CloseBilmottakModal } from './CloseBilmottakModal';
import { OrderTypeBadge } from './OrderTypeBadge';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

// Tab type for switching between views
type ReceivingTab = 'orders' | 'shipments' | 'active';

// Interface for POrder data som brukes i komponenten
interface POrder {
  id: number;
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  orderType?: string | null;
  delCustomerName: string;
  orderDate: string;
  supplierName: string;
  warehouse: string;
  supplierNo: string;
  supplierOrderNo: string;
  orderFreeField1: string;
  orderFreeField2: string;
  orderFreeField3: string;
  projectNo?: string | null;
  depNo?: string | null;
  numOfLines: number;
}

// Funksjon for å få status-tekst basert på enum-verdier
const getStatusText = (status: number, t: (key: string) => string): string => {
  switch (status) {
    case POrderStatus.Ubehandlet: return t("warehouse.modules.receipt.status.unprocessed");
    case POrderStatus.Godkjent: return t("warehouse.modules.receipt.status.approved");
    case POrderStatus.UnderArbeid: return t("warehouse.modules.receipt.status.inProgress");
    case POrderStatus.BehandletIkkeKomplett: return t("warehouse.modules.receipt.status.processedIncomplete");
    case POrderStatus.BehandletKomplett: return t("warehouse.modules.receipt.status.processedComplete");
    case POrderStatus.TilPakking: return t("warehouse.modules.receipt.status.toPacking");
    case POrderStatus.KlarForEksport: return t("warehouse.modules.receipt.status.readyForExport");
    case POrderStatus.Eksportert: return t("warehouse.modules.receipt.status.exported");
    default: return t("warehouse.modules.receipt.status.unknown");
  }
};

// Funksjon for å få kort status-tekst for mobile
const getShortStatusText = (status: number, t: (key: string) => string): string => {
  switch (status) {
    case POrderStatus.Ubehandlet: return t("warehouse.modules.receipt.status.short.unprocessed");
    case POrderStatus.Godkjent: return t("warehouse.modules.receipt.status.short.approved");
    case POrderStatus.UnderArbeid: return t("warehouse.modules.receipt.status.short.inProgress");
    case POrderStatus.BehandletIkkeKomplett: return t("warehouse.modules.receipt.status.short.processedIncomplete");
    case POrderStatus.BehandletKomplett: return t("warehouse.modules.receipt.status.short.processedComplete");
    case POrderStatus.TilPakking: return t("warehouse.modules.receipt.status.short.toPacking");
    case POrderStatus.KlarForEksport: return t("warehouse.modules.receipt.status.short.readyForExport");
    case POrderStatus.Eksportert: return t("warehouse.modules.receipt.status.short.exported");
    default: return `${status}`;
  }
};

// Funksjon for å få status-farge og ikon
const getStatusStyle = (status: number) => {
  switch (status) {
    case POrderStatus.Ubehandlet: return { color: 'text-gray-600', bg: 'bg-gray-100', icon: AlertCircle };
    case POrderStatus.Godkjent: return { color: 'text-blue-600', bg: 'bg-blue-100', icon: CheckCircle };
    case POrderStatus.UnderArbeid: return { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: Clock };
    case POrderStatus.BehandletIkkeKomplett: return { color: 'text-orange-600', bg: 'bg-orange-100', icon: Package };
    case POrderStatus.BehandletKomplett: return { color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle };
    case POrderStatus.TilPakking: return { color: 'text-purple-600', bg: 'bg-purple-100', icon: Package };
    case POrderStatus.KlarForEksport: return { color: 'text-indigo-600', bg: 'bg-indigo-100', icon: Truck };
    default: return { color: 'text-gray-600', bg: 'bg-gray-100', icon: AlertCircle };
  }
};

// Order Card Component for mobile - Compact 2-line design matching PickingCard
const OrderCard: React.FC<{ order: POrder; onSelect: (orderNo: number) => void; onPreview: (orderNo: number) => void; t: (key: string) => string }> = ({ order, onSelect, onPreview, t }) => {
  const statusStyle = getStatusStyle(order.orderStatus);
  const StatusIcon = statusStyle.icon;

  // Build Line 1: orderNoERP + supplierOrderNo + orderFreeField1 + orderFreeField2 + projectNo + depNo (only non-empty)
  const line1Parts = [
    order.orderNoERP,
    order.supplierOrderNo !== t("common.fields.notSpecified") ? order.supplierOrderNo : '',
    order.orderFreeField1 !== t("common.fields.notSpecified") ? order.orderFreeField1 : '',
    order.orderFreeField2 !== t("common.fields.notSpecified") ? order.orderFreeField2 : '',
    order.projectNo ? `${t("warehouse.modules.receipt.list.projectPrefix")}: ${order.projectNo}` : '',
    order.depNo ? `${t("warehouse.modules.receipt.list.departmentPrefix")}: ${order.depNo}` : ''
  ].filter(part => part && part.trim().length > 0);

  const line1Text = line1Parts.join(' | ');

  return (
    <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-3 hover:shadow-md hover:border-gray-300 transition-all duration-200">
      {/* Linje 1: ordre-identifikatorer + dato + antall + status.
          Dato og antall er flyttet hit opp fra linje 2 (TW 36335735) slik at
          leverandoernavnet faar nesten hele linje 2 for seg selv — det ble kraftig
          avkortet ("Tekno...", "Ansell ...") mens linje 1 sto halvtom.

          NB: line1Text er ikke bare ordrenummeret. Den settes sammen av inntil seks
          felter (orderNoERP, supplierOrderNo, orderFreeField1/2, projectNo, depNo).
          Hos SafeNor er de oevrige tomme, men hos andre kunder kan linja bli lang —
          derfor har dato og antall flex-shrink-0 mens line1Text beholder truncate. */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-xs text-gray-600 truncate flex-1 min-w-0">
          {line1Text}
        </div>
        <span className="text-xs text-gray-500 flex-shrink-0">{order.orderDate}</span>
        <div className="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0">
          <Package size={12} className="text-gray-400" />
          <span className="font-medium">{order.numOfLines}</span>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.color} flex-shrink-0`}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {getShortStatusText(order.orderStatus, t)}
        </span>
      </div>

      {/* Linje 2: leverandoernavn (BOLD) — faar naa nesten hele bredden — + ordretype + knapper.
          flex-wrap: ordretypen (TW 36420288) brytes til egen linje paa smale skjermer i stedet for aa kuttes. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 flex-1 min-w-0">
          <span className="text-xs font-bold text-gray-900 truncate min-w-0 max-w-full">{order.supplierName}</span>
          <OrderTypeBadge orderType={order.orderType} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onPreview(order.orderNo)}
            className="flex items-center gap-1 bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-300 transition-colors"
          >
            <Eye size={12} />
            <span>{t("common.buttons.view")}</span>
          </button>
          <button
            onClick={() => onSelect(order.orderNo)}
            className="flex items-center gap-1 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 transition-colors"
          >
            <span>{t("warehouse.modules.receipt.start")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Table Row Component for desktop - Compact 2-line design matching PickingCard
const OrderTableRow: React.FC<{ order: POrder; onSelect: (orderNo: number) => void; onPreview: (orderNo: number) => void; t: (key: string) => string }> = ({ order, onSelect, onPreview, t }) => {
  const statusStyle = getStatusStyle(order.orderStatus);
  const StatusIcon = statusStyle.icon;

  // Build Line 1: orderNoERP + supplierOrderNo + orderFreeField1 + orderFreeField2 + projectNo + depNo (only non-empty)
  const line1Parts = [
    order.orderNoERP,
    order.supplierOrderNo !== t("common.fields.notSpecified") ? order.supplierOrderNo : '',
    order.orderFreeField1 !== t("common.fields.notSpecified") ? order.orderFreeField1 : '',
    order.orderFreeField2 !== t("common.fields.notSpecified") ? order.orderFreeField2 : '',
    order.projectNo ? `${t("warehouse.modules.receipt.list.projectPrefix")}: ${order.projectNo}` : '',
    order.depNo ? `${t("warehouse.modules.receipt.list.departmentPrefix")}: ${order.depNo}` : ''
  ].filter(part => part && part.trim().length > 0);

  const line1Text = line1Parts.join(' | ');

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 max-w-0 w-full">
        {/* Line 1: Combined order identifiers */}
        <div className="text-xs text-gray-600 truncate" title={line1Text}>
          {line1Text}
        </div>
        {/* Line 2: supplierName (BOLD) + orderDate + numOfLines + ordretype (TW 36420288) */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 min-w-0">
          <span className="text-xs font-bold text-gray-900 truncate min-w-0 max-w-full">{order.supplierName}</span>
          <span className="text-xs text-gray-500 flex-shrink-0">{order.orderDate}</span>
          <div className="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0">
            <Package size={12} className="text-gray-400" />
            <span className="font-medium">{order.numOfLines}</span>
          </div>
          <OrderTypeBadge orderType={order.orderType} />
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.color}`}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {getStatusText(order.orderStatus, t)}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPreview(order.orderNo)}
            className="inline-flex items-center px-2 lg:px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
          >
            <Eye size={14} className="mr-1" />
            <span>{t("common.buttons.view")}</span>
          </button>
          <button
            onClick={() => onSelect(order.orderNo)}
            className="inline-flex items-center px-2 lg:px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <span>{t("warehouse.modules.receipt.start")}</span>
          </button>
        </div>
      </td>
    </tr>
  );
};

interface POrdersReceivingListProps {
  onOrderSelect: (orderNo: number) => void;
  onBack?: () => void;
  onStartOpenReceiving?: () => void;
  /** Callback to open the "incomplete receipts" report (Norlux punkt 17) */
  onShowIncompleteReceipts?: () => void;
  /** Callback when a shipment is selected (Modus 3: Inbound Shipment) */
  onShipmentSelect?: (shipmentNo: string) => void;
  /** Callback when a Bilmottak session is selected/joined (Modus 4: Truck Receiving) */
  onBilmottakSelect?: (batchId: string, name: string, warehouse: string) => void;
}

export const POrdersReceivingList: React.FC<POrdersReceivingListProps> = ({ onOrderSelect, onBack, onStartOpenReceiving, onShowIncompleteReceipts, onShipmentSelect, onBilmottakSelect }) => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();
  const [orders, setOrders] = useState<POrder[]>([]);
  const [shipments, setShipments] = useState<InboundShipmentSummaryDto[]>([]);
  const [bilmottakSessions, setBilmottakSessions] = useState<ActiveBilmottakSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [bilmottakLoading, setBilmottakLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewOrderNo, setPreviewOrderNo] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<ReceivingTab>('orders');
  const [showNewBilmottakModal, setShowNewBilmottakModal] = useState(false);
  const [showCloseBilmottakModal, setShowCloseBilmottakModal] = useState(false);
  const [closingSession, setClosingSession] = useState<ActiveBilmottakSessionDto | null>(null);

  // Item number search state
  const [itemSearchOrders, setItemSearchOrders] = useState<POrder[]>([]);
  const [itemSearching, setItemSearching] = useState(false);
  const itemSearchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Felles funksjon for å hente porders fra backend API via service
  // Bruker pOrdersService som håndterer autentisering automatisk
  const fetchOrdersFromAPI = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Bruk pOrdersService for å hente data med autentisering
      const data: POrdersDto[] = await pOrdersService.getOrdersForReceiving(currentWarehouse || undefined);
      
      // Konverter API-respons til format som komponenten forventer
      const formattedOrders: POrder[] = data.map((order: POrdersDto) => ({
        id: order.id,
        orderNo: order.orderNo,
        orderNoERP: order.orderNoERP,
        orderStatus: order.orderStatus,
        orderType: order.orderType || null,
        delCustomerName: order.delCustomerName || t("common.fields.notSpecified"),
        orderDate: order.orderDate ? new Date(order.orderDate).toLocaleDateString('nb-NO') : t("common.fields.notSpecified"),
        supplierName: order.supplierName || t("common.fields.notSpecified"),
        warehouse: order.warehouse || t("common.fields.notSpecified"),
        supplierNo: order.supplierNo || t("common.fields.notSpecified"),
        supplierOrderNo: order.supplierOrderNo || t("common.fields.notSpecified"),
        orderFreeField1: order.orderFreeField1 || t("common.fields.notSpecified"),
        orderFreeField2: order.orderFreeField2 || t("common.fields.notSpecified"),
        orderFreeField3: order.orderFreeField3 || t("common.fields.notSpecified"),
        projectNo: order.projectNo || null,
        depNo: order.depNo || null,
        numOfLines: order.numOfLines || 0
      }));
      
      setOrders(formattedOrders);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching orders from API:', err);
      setError(t("warehouse.modules.receipt.errors.fetchFailed", { error: err instanceof Error ? err.message : t("common.errors.unknownError") }));
      setLoading(false);
    }
  };

  // Funksjon for å hente inbound shipments fra backend API
  const fetchShipmentsFromAPI = async () => {
    try {
      setShipmentsLoading(true);
      const data = await pOrderLineService.getActiveShipments();
      setShipments(data);
      setShipmentsLoading(false);
    } catch (err) {
      console.error('Error fetching shipments from API:', err);
      // Don't set error state here, just log - shipments are optional
      setShipmentsLoading(false);
    }
  };

  // Funksjon for å hente aktive Bilmottak-sesjoner fra backend API
  const fetchBilmottakSessionsFromAPI = async () => {
    try {
      setBilmottakLoading(true);
      const data = await bilmottakService.getActiveSessions();
      setBilmottakSessions(data);
      setBilmottakLoading(false);
    } catch (err) {
      console.error('Error fetching Bilmottak sessions from API:', err);
      // Don't set error state here, just log - bilmottak is optional
      setBilmottakLoading(false);
    }
  };

  // Handler for when a new Bilmottak session is created
  const handleBilmottakSessionCreated = (batchId: string, name: string, warehouse: string) => {
    fetchBilmottakSessionsFromAPI();
    if (onBilmottakSelect) {
      onBilmottakSelect(batchId, name, warehouse);
    }
  };

  // Handler for joining an existing Bilmottak session
  const handleJoinBilmottak = async (session: ActiveBilmottakSessionDto) => {
    try {
      const response = await bilmottakService.joinSession(session.batchId);
      if (response.success && onBilmottakSelect) {
        onBilmottakSelect(session.batchId, session.name, session.warehouse);
      } else if (!response.success) {
        console.error('Failed to join Bilmottak session:', response.errorMessage);
      }
    } catch (err) {
      console.error('Error joining Bilmottak session:', err);
    }
  };

  // Hent porders, shipments og bilmottak for varemottak fra backend API ved komponentstart —
  // og på nytt når valgt lager endres. Forsendelser og Bilmottak sendes uten lager: de viser
  // alle lagre som før, og backend begrenser bare låste profiler (TW 36430604)
  useEffect(() => {
    fetchOrdersFromAPI();
    fetchShipmentsFromAPI();
    fetchBilmottakSessionsFromAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWarehouse]);

  // Debounced item number search — triggers API call when search term doesn't match headers
  useEffect(() => {
    // Clear previous timer
    if (itemSearchTimerRef.current) {
      clearTimeout(itemSearchTimerRef.current);
    }

    const term = searchTerm.trim();

    // Need at least 2 characters to search
    if (term.length < 2) {
      setItemSearchOrders([]);
      setItemSearching(false);
      return;
    }

    // Check if there are header-level matches already
    const hasHeaderMatches = orders.some(order =>
      order.orderNoERP.toLowerCase().includes(term.toLowerCase()) ||
      order.supplierOrderNo.toLowerCase().includes(term.toLowerCase()) ||
      order.orderFreeField1.toLowerCase().includes(term.toLowerCase()) ||
      order.orderFreeField2.toLowerCase().includes(term.toLowerCase()) ||
      order.supplierName.toLowerCase().includes(term.toLowerCase())
    );

    // If header matches exist, skip item search
    if (hasHeaderMatches) {
      setItemSearchOrders([]);
      setItemSearching(false);
      return;
    }

    // Debounce: wait 400ms before calling API
    setItemSearching(true);
    itemSearchTimerRef.current = setTimeout(async () => {
      try {
        const data: POrdersDto[] = await pOrdersService.getOrdersForReceiving(
          currentWarehouse || undefined,
          term
        );
        const formattedOrders: POrder[] = data.map((order: POrdersDto) => ({
          id: order.id,
          orderNo: order.orderNo,
          orderNoERP: order.orderNoERP,
          orderStatus: order.orderStatus,
          orderType: order.orderType || null,
          delCustomerName: order.delCustomerName || t("common.fields.notSpecified"),
          orderDate: order.orderDate ? new Date(order.orderDate).toLocaleDateString('nb-NO') : t("common.fields.notSpecified"),
          supplierName: order.supplierName || t("common.fields.notSpecified"),
          warehouse: order.warehouse || t("common.fields.notSpecified"),
          supplierNo: order.supplierNo || t("common.fields.notSpecified"),
          supplierOrderNo: order.supplierOrderNo || t("common.fields.notSpecified"),
          orderFreeField1: order.orderFreeField1 || t("common.fields.notSpecified"),
          orderFreeField2: order.orderFreeField2 || t("common.fields.notSpecified"),
          orderFreeField3: order.orderFreeField3 || t("common.fields.notSpecified"),
          numOfLines: order.numOfLines || 0
        }));
        setItemSearchOrders(formattedOrders);
      } catch (err) {
        console.error('Error searching orders by item number:', err);
        setItemSearchOrders([]);
      } finally {
        setItemSearching(false);
      }
    }, 400);

    return () => {
      if (itemSearchTimerRef.current) {
        clearTimeout(itemSearchTimerRef.current);
      }
    };
  }, [searchTerm, orders, currentWarehouse]);

  // Filtrer ordrer basert på søketerm og status
  const headerFilteredOrders = orders.filter(order => {
    const matchesSearch = !searchTerm.trim() ||
      order.orderNoERP.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.supplierOrderNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderFreeField1.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderFreeField2.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.projectNo?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (order.depNo?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);

    const matchesStatus = statusFilter === 'all' || order.orderStatus.toString() === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Combine header matches + item number search results (dedup by orderNo)
  const filteredOrders = (() => {
    if (itemSearchOrders.length === 0) return headerFilteredOrders;

    const headerOrderNos = new Set(headerFilteredOrders.map(o => o.orderNo));
    const itemOnlyOrders = itemSearchOrders
      .filter(o => !headerOrderNos.has(o.orderNo))
      .filter(o => statusFilter === 'all' || o.orderStatus.toString() === statusFilter);

    return [...headerFilteredOrders, ...itemOnlyOrders];
  })();

  const handleRefresh = async () => {
    await fetchOrdersFromAPI();
    await fetchShipmentsFromAPI();
    await fetchBilmottakSessionsFromAPI();
  };

  // Filtrer forsendelser basert på søketerm
  const filteredShipments = shipments.filter(shipment => {
    const matchesSearch =
      shipment.inboundShipmentNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.supplierNames.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.supplierNos.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handlePreviewOrder = (orderNo: number) => {
    setPreviewOrderNo(orderNo);
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    setPreviewOrderNo(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-5 w-5 sm:h-6 sm:w-6 animate-spin text-blue-500" />
          <span className="text-base sm:text-lg text-gray-600">{t("warehouse.modules.receipt.loadingOrders")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6">
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm sm:text-base">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Header. Raden må kunne brytes på håndholdte terminaler (~360 px) — uten flex-wrap
          setter knappene en minimumsbredde for hele siden. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-4">
        <BackButton onClick={onBack || (() => {})} hideLabelOnMobile />
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">
          {t("warehouse.modules.receipt.title")}
        </h1>
        <div className="ml-auto flex items-center gap-2">
          {onShowIncompleteReceipts && (
            <button
              onClick={onShowIncompleteReceipts}
              title={t("warehouse.modules.receipt.reports.incompleteReceipts")}
              className="flex items-center justify-center gap-2 min-h-[44px] px-3 sm:px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline">{t("warehouse.modules.receipt.reports.incompleteReceipts")}</span>
              <span className="sm:hidden">{t("warehouse.modules.receipt.reports.incompleteReceiptsShort", {}, 'Ufullstendige')}</span>
            </button>
          )}
          <button
            onClick={handleRefresh}
            aria-label={t("common.buttons.refresh")}
            title={t("common.buttons.refresh")}
            className="flex items-center justify-center gap-2 min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
          >
            <RefreshCw className="h-4 w-4 flex-shrink-0" />
            <span className="hidden sm:inline">{t("common.buttons.refresh")}</span>
          </button>
        </div>
      </div>

      {/* Tabs. Under `sm` (håndholdte terminaler) vises kun ikon + antall på inaktive faner —
          den aktive fanen beholder etiketten, så operatøren alltid ser hvor hen står. Fane-raden
          scroller i seg selv (flex-nowrap + overflow-x-auto) slik at den aldri utvider siden. */}
      <div className="border-b border-gray-200">
        <nav
          className="-mb-px flex flex-nowrap items-center gap-3 sm:gap-8 overflow-x-auto scrollbar-hide"
          aria-label={t("warehouse.modules.receipt.list.tabsAriaLabel")}
        >
          <button
            onClick={() => setActiveTab('orders')}
            aria-label={t("warehouse.modules.receipt.purchaseOrders")}
            title={t("warehouse.modules.receipt.purchaseOrders")}
            className={`flex-shrink-0 flex items-center gap-2 min-h-[44px] py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'orders'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="h-4 w-4 flex-shrink-0" />
            <span className={activeTab === 'orders' ? '' : 'hidden sm:inline'}>
              {t("warehouse.modules.receipt.purchaseOrders")}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'orders' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
            }`}>
              {filteredOrders.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('shipments')}
            aria-label={t('warehouse.modules.receipt.shipments')}
            title={t('warehouse.modules.receipt.shipments')}
            className={`flex-shrink-0 flex items-center gap-2 min-h-[44px] py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'shipments'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Ship className="h-4 w-4 flex-shrink-0" />
            <span className={activeTab === 'shipments' ? '' : 'hidden sm:inline'}>
              {t('warehouse.modules.receipt.shipments')}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'shipments' ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-600'
            }`}>
              {filteredShipments.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('active')}
            aria-label={t('warehouse.modules.consolidatedReceiving.shortName')}
            title={t('warehouse.modules.consolidatedReceiving.shortName')}
            className={`flex-shrink-0 flex items-center gap-2 min-h-[44px] py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'active'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Truck className="h-4 w-4 flex-shrink-0" />
            <span className={activeTab === 'active' ? '' : 'hidden sm:inline'}>
              {t('warehouse.modules.consolidatedReceiving.shortName')}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              activeTab === 'active' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'
            }`}>
              {bilmottakSessions.length}
            </span>
          </button>
          {/* Fritt mottak: handlingsflik som går rett inn i fri-modus (auto-gjenoppta aktivt mottak).
              Denne blir aldri «aktiv», så etiketten står alltid — en naken ikonknapp ville vært ulesbar. */}
          {onStartOpenReceiving && (
            <button
              onClick={onStartOpenReceiving}
              className="flex-shrink-0 flex items-center gap-2 min-h-[44px] py-3 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 hover:text-emerald-700 hover:border-emerald-300 transition-colors"
            >
              <ScanLine className="h-4 w-4 flex-shrink-0" />
              <span className="whitespace-nowrap">{t('warehouse.modules.receipt.freeReceivingTab', {}, 'Fritt mottak')}</span>
            </button>
          )}
        </nav>
      </div>

      {/* Filters */}
      <div className="flex gap-3 sm:gap-4">
        {/* min-w-0: uten den kan ikke søkefeltet krympe under sin egen intrinsiske bredde (~170 px),
            og filterraden blir en minimumsbredde for siden på håndholdte terminaler. */}
        <div className="flex-1 min-w-0 relative">
          {itemSearching ? (
            <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-500 animate-spin" />
          ) : (
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          )}
          <input
            type="text"
            placeholder={t("warehouse.modules.receipt.list.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="appearance-none bg-white px-3 sm:px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-w-[140px]"
          >
            <option value="all">{t("warehouse.modules.receipt.filters.allStatuses")}</option>
            <option value={POrderStatus.Ubehandlet.toString()}>{t("warehouse.modules.receipt.status.unprocessed")}</option>
            <option value={POrderStatus.Godkjent.toString()}>{t("warehouse.modules.receipt.status.approved")}</option>
            <option value={POrderStatus.UnderArbeid.toString()}>{t("warehouse.modules.receipt.status.inProgress")}</option>
            <option value={POrderStatus.BehandletIkkeKomplett.toString()}>{t("warehouse.modules.receipt.status.short.processedIncomplete")}</option>
            <option value={POrderStatus.BehandletKomplett.toString()}>{t("warehouse.modules.receipt.status.short.processedComplete")}</option>
            <option value={POrderStatus.TilPakking.toString()}>{t("warehouse.modules.receipt.status.toPacking")}</option>
            <option value={POrderStatus.KlarForEksport.toString()}>{t("warehouse.modules.receipt.status.readyForExport")}</option>
          </select>
          <Filter className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'orders' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Item search indicator */}
          {itemSearching && (
            <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 flex items-center space-x-2 text-sm text-blue-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t("warehouse.modules.receipt.list.searchingItemInLines", { 0: searchTerm.trim() })}</span>
            </div>
          )}
          {itemSearchOrders.length > 0 && !itemSearching && (
            <div className="px-3 py-2 bg-green-50 border-b border-green-200 flex items-center space-x-2 text-sm text-green-700">
              <Search className="h-4 w-4" />
              <span>{t("warehouse.modules.receipt.list.foundOrdersWithItem", { 0: itemSearchOrders.length, 1: searchTerm.trim() })}</span>
            </div>
          )}
          {/* Mobile Card Layout */}
          <div className="block lg:hidden p-3 space-y-3">
            {filteredOrders.map((order) => (
              <OrderCard key={order.id} order={order} onSelect={onOrderSelect} onPreview={handlePreviewOrder} t={t} />
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("warehouse.modules.receipt.table.orderSupplier")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("common.fields.status")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("warehouse.modules.receipt.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <OrderTableRow key={order.id} order={order} onSelect={onOrderSelect} onPreview={handlePreviewOrder} t={t} />
                ))}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && !itemSearching && (
            <div className="text-center py-8 sm:py-12">
              <Package className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t("warehouse.modules.receipt.noOrdersFound")}</h3>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? t("warehouse.modules.receipt.noOrdersMatchCriteria")
                  : t("warehouse.modules.receipt.noOrdersAvailable")
                }
              </p>
              {(searchTerm || statusFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                >
                  {t("warehouse.modules.receipt.clearFilters")}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Shipments Tab Content (Modus 3: Inbound Shipment) */}
      {activeTab === 'shipments' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {shipmentsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-5 w-5 animate-spin text-teal-500 mr-2" />
              <span className="text-gray-600">{t("warehouse.modules.receipt.list.loadingShipments")}</span>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout for Shipments */}
              <div className="block lg:hidden p-3 space-y-3">
                {filteredShipments.map((shipment) => (
                  <div
                    key={shipment.inboundShipmentNo}
                    className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-3 hover:shadow-md hover:border-teal-300 transition-all duration-200"
                  >
                    {/* Line 1: Shipment ID + Status Badge */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900 truncate">
                        <Ship className="inline h-4 w-4 mr-1 text-teal-500" />
                        {shipment.inboundShipmentNo}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                        {Math.round(shipment.completionPercentage)}%
                      </span>
                    </div>
                    {/* Line 2: Suppliers + Line Count + Button */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-xs text-gray-600 truncate">{shipment.supplierNames}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Package size={12} className="text-gray-400" />
                          <span className="font-medium">{shipment.lineCount}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <span>{shipment.remainingQuantity.toFixed(0)}</span>
                          <span className="text-gray-400">/</span>
                          <span>{shipment.totalQuantity.toFixed(0)}</span>
                        </div>
                        {onShipmentSelect && (
                          <button
                            onClick={() => onShipmentSelect(shipment.inboundShipmentNo)}
                            className="flex items-center gap-1 bg-teal-600 text-white px-2 py-1 rounded text-xs hover:bg-teal-700 transition-colors"
                          >
                            <span>{t("warehouse.modules.receipt.start")}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table Layout for Shipments */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("warehouse.modules.receipt.list.shipmentHeader")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("warehouse.modules.receipt.list.suppliersHeader")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("warehouse.modules.receipt.list.linesOrdersHeader")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("warehouse.modules.receipt.list.remainingHeader")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("warehouse.modules.receipt.list.progressHeader")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("warehouse.modules.receipt.list.actionsHeader")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredShipments.map((shipment) => (
                      <tr key={shipment.inboundShipmentNo} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Ship className="h-4 w-4 text-teal-500" />
                            <span className="text-sm font-medium text-gray-900">{shipment.inboundShipmentNo}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600 truncate block max-w-xs" title={shipment.supplierNames}>
                            {shipment.supplierNames}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="font-medium">{shipment.lineCount}</span>
                            <span className="text-gray-400">/</span>
                            <span>{t("warehouse.modules.receipt.list.orderCount", { 0: shipment.orderCount })}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {t("warehouse.modules.receipt.list.remainingOfTotal", { 0: shipment.remainingQuantity.toFixed(0), 1: shipment.totalQuantity.toFixed(0) })}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-teal-500 h-2 rounded-full transition-all"
                                style={{ width: `${Math.min(100, shipment.completionPercentage)}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600">{Math.round(shipment.completionPercentage)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {onShipmentSelect && (
                            <button
                              onClick={() => onShipmentSelect(shipment.inboundShipmentNo)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 transition-colors"
                            >
                              {t("warehouse.modules.receipt.list.startReceiving")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredShipments.length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <Ship className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">{t("warehouse.modules.receipt.list.noShipmentsFound")}</h3>
                  <p className="mt-1 text-xs sm:text-sm text-gray-500">
                    {searchTerm
                      ? t("warehouse.modules.receipt.list.noShipmentsMatch")
                      : t("warehouse.modules.receipt.list.noShipmentsAvailable")
                    }
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Active Receiving Tab Content (Modus 4: Bilmottak) */}
      {activeTab === 'active' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Header with "New Session" button */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-purple-600" />
              <span className="font-medium text-gray-900">{t('warehouse.modules.consolidatedReceiving.activeSessions')}</span>
            </div>
            <button
              onClick={() => setShowNewBilmottakModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">{t('warehouse.modules.consolidatedReceiving.newSession')}</span>
            </button>
          </div>

          {bilmottakLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-5 w-5 animate-spin text-purple-500 mr-2" />
              <span className="text-gray-600">{t('warehouse.modules.consolidatedReceiving.loadingSessions')}</span>
            </div>
          ) : bilmottakSessions.length > 0 ? (
            <>
              {/* Mobile Card Layout for Bilmottak */}
              <div className="block lg:hidden p-3 space-y-3">
                {bilmottakSessions.map((session) => (
                  <div
                    key={session.batchId}
                    className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-3 hover:shadow-md hover:border-purple-300 transition-all duration-200"
                  >
                    {/* Line 1: Session name + Operator count badge */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900 truncate">
                        <Truck className="inline h-4 w-4 mr-1 text-purple-500" />
                        {session.name}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        <Users className="h-3 w-3" />
                        {session.activeOperatorCount}
                      </span>
                    </div>
                    {/* Line 2: Warehouse + Lines + Button */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-xs text-gray-600 truncate">{session.warehouse}</span>
                        <span className="text-xs text-gray-400">|</span>
                        <span className="text-xs text-gray-600">{session.totalLinesReceived} {t('warehouse.modules.consolidatedReceiving.lines')}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {session.isCurrentUserParticipating ? (
                          <button
                            onClick={() => handleJoinBilmottak(session)}
                            className="flex items-center gap-1 bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700 transition-colors"
                          >
                            <span>{t('common.buttons.continue')}</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleJoinBilmottak(session)}
                            className="flex items-center gap-1 bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700 transition-colors"
                          >
                            <span>{t('warehouse.modules.consolidatedReceiving.joinSession')}</span>
                          </button>
                        )}
                        <button
                          onClick={() => { setClosingSession(session); setShowCloseBilmottakModal(true); }}
                          className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-200 transition-colors border border-gray-300"
                        >
                          <CheckCircle className="h-3 w-3" />
                          <span>{t("warehouse.modules.receipt.actions.finish")}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table Layout for Bilmottak */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.consolidatedReceiving.session')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.consolidatedReceiving.warehouse')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.consolidatedReceiving.operators')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.consolidatedReceiving.progress')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.consolidatedReceiving.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bilmottakSessions.map((session) => (
                      <tr key={session.batchId} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-purple-500" />
                            <div>
                              <span className="text-sm font-medium text-gray-900">{session.name}</span>
                              <span className="block text-xs text-gray-500">
                                {t('warehouse.modules.consolidatedReceiving.created')}: {new Date(session.createdAt).toLocaleString('nb-NO', {
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{session.warehouse}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                              <Users className="h-3 w-3" />
                              {session.activeOperatorCount} {t('warehouse.modules.consolidatedReceiving.active')}
                            </span>
                            {session.isCurrentUserParticipating && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                {t('warehouse.modules.consolidatedReceiving.youAreParticipating')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">{session.totalLinesReceived}</span> {t('warehouse.modules.consolidatedReceiving.lines')}
                            {session.ordersAffected > 0 && (
                              <span className="text-gray-400"> | {session.ordersAffected} {t('warehouse.modules.consolidatedReceiving.orders')}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleJoinBilmottak(session)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                            >
                              {session.isCurrentUserParticipating ? t('common.buttons.continue') : t('warehouse.modules.consolidatedReceiving.joinSession')}
                            </button>
                            <button
                              onClick={() => { setClosingSession(session); setShowCloseBilmottakModal(true); }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                            >
                              <CheckCircle className="h-3 w-3" />
                              {t("warehouse.modules.receipt.actions.finish")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <Truck className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('warehouse.modules.consolidatedReceiving.noActiveSessions')}</h3>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">
                {t('warehouse.modules.consolidatedReceiving.multiUserDescription')}
              </p>
              <button
                onClick={() => setShowNewBilmottakModal(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                <span>{t('warehouse.modules.consolidatedReceiving.startNewSession')}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      <PreviewPOrderModal
        isOpen={showPreviewModal}
        orderNo={previewOrderNo}
        onClose={handleClosePreview}
      />

      {/* New Bilmottak Modal */}
      <NewBilmottakModal
        isOpen={showNewBilmottakModal}
        onClose={() => setShowNewBilmottakModal(false)}
        onSessionCreated={handleBilmottakSessionCreated}
      />

      {/* Close Bilmottak Modal */}
      {closingSession && (
        <CloseBilmottakModal
          isOpen={showCloseBilmottakModal}
          onClose={() => { setShowCloseBilmottakModal(false); setClosingSession(null); }}
          session={closingSession}
          onClosed={() => {
            fetchBilmottakSessionsFromAPI();
            setClosingSession(null);
          }}
        />
      )}
    </div>
  );
};
